import React, { useState, useEffect } from 'react';
import './Dashboard.css';
import { apiCall, API_URL } from '../utils/api';

const AdminGalleryManagement = () => {
  const [categories, setCategories] = useState([]);
  const [images, setImages] = useState([]);
  const [showCategoryForm, setShowCategoryForm] = useState(false);
  const [showImageForm, setShowImageForm] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [editingImage, setEditingImage] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('all');

  const [categoryForm, setCategoryForm] = useState({
    name: '',
    displayName: '',
    enabled: true,
    displayOrder: 0
  });

  const [imageForm, setImageForm] = useState({
    category: '',
    title: '',
    altText: '',
    imageUrl: '',
    displayOrder: 0,
    enabled: true
  });

  // Load data from API
  useEffect(() => {
    // Keep categories static or load from DB if you create a Category model.
    // For now, let's keep the user's existing static category logic or just hardcode some defaults if they are not editing them frequently.
    // But better to at least preserve localStorage for categories if backend doesn't support them yet.
    const savedCategories = JSON.parse(localStorage.getItem('galleryCategories') || '[]');
    if (savedCategories.length === 0) {
      const defaultCategories = [
        { id: 'yoga', name: 'yoga', displayName: 'Yoga', enabled: true, displayOrder: 1 },
        { id: 'meditation', name: 'meditation', displayName: 'Meditation', enabled: true, displayOrder: 2 },
        { id: 'events', name: 'events', displayName: 'Events', enabled: true, displayOrder: 3 },
        { id: 'workshops', name: 'workshops', displayName: 'Workshops', enabled: true, displayOrder: 4 },
        { id: 'retreats', name: 'retreats', displayName: 'Retreats', enabled: true, displayOrder: 5 }
      ];
      setCategories(defaultCategories);
      localStorage.setItem('galleryCategories', JSON.stringify(defaultCategories));
    } else {
      setCategories(savedCategories);
    }

    fetchImages();
  }, []);

  const fetchImages = async () => {
    try {
      const data = await apiCall('/gallery');
      const mapped = data.map(img => ({
        id: img._id,
        category: img.category,
        title: img.title,
        altText: img.altText,
        imageUrl: img.image ? (img.image.startsWith('http') ? img.image : `${API_URL.replace('/api', '')}/${img.image}`) : '',
        displayOrder: img.displayOrder,
        enabled: img.enabled
      }));
      setImages(mapped);
    } catch (error) {
      console.error("Failed to fetch gallery", error);
    }
  };

  // Category Handlers
  const handleAddCategory = () => {
    setCategoryForm({ name: '', displayName: '', enabled: true, displayOrder: categories.length + 1 });
    setEditingCategory(null);
    setShowCategoryForm(true);
  };

  const handleEditCategory = (category) => {
    setCategoryForm(category);
    setEditingCategory(category.id);
    setShowCategoryForm(true);
  };

  const handleSaveCategory = () => {
    if (!categoryForm.name || !categoryForm.displayName) {
      alert('Please fill all required fields');
      return;
    }

    let updatedCategories;
    if (editingCategory) {
      updatedCategories = categories.map(c =>
        c.id === editingCategory ? { ...categoryForm, id: editingCategory } : c
      );
    } else {
      const newCategory = {
        ...categoryForm,
        id: Date.now(),
        name: categoryForm.name.toLowerCase().replace(/\s+/g, '-')
      };
      updatedCategories = [...categories, newCategory];
    }

    setCategories(updatedCategories);
    localStorage.setItem('galleryCategories', JSON.stringify(updatedCategories));
    setShowCategoryForm(false);
    setCategoryForm({ name: '', displayName: '', enabled: true, displayOrder: 0 });
    alert('Category saved successfully!');
  };

  const handleDeleteCategory = (id) => {
    if (window.confirm('Are you sure? This will also delete all images in this category.')) {
      const updatedCategories = categories.filter(c => c.id !== id);
      const category = categories.find(c => c.id === id);
      const updatedImages = images.filter(img => img.category !== category.name);

      setCategories(updatedCategories);
      setImages(updatedImages);
      localStorage.setItem('galleryCategories', JSON.stringify(updatedCategories));
      localStorage.setItem('galleryImages', JSON.stringify(updatedImages));
      alert('Category deleted successfully!');
    }
  };

  const toggleCategoryStatus = (id) => {
    const updatedCategories = categories.map(c =>
      c.id === id ? { ...c, enabled: !c.enabled } : c
    );
    setCategories(updatedCategories);
    localStorage.setItem('galleryCategories', JSON.stringify(updatedCategories));
  };

  // Image Handlers
  const handleAddImage = () => {
    setImageForm({ category: '', title: '', altText: '', imageUrl: '', displayOrder: images.length + 1, enabled: true });
    setEditingImage(null);
    setShowImageForm(true);
  };

  const handleEditImage = (image) => {
    setImageForm(image);
    setEditingImage(image.id);
    setShowImageForm(true);
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setImageForm({ ...imageForm, imageUrl: reader.result, imageFile: file });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSaveImage = async () => {
    if (!imageForm.category || !imageForm.title) {
      alert('Please fill all required fields');
      return;
    }

    const formData = new FormData();
    formData.append('category', imageForm.category);
    formData.append('title', imageForm.title);
    formData.append('altText', imageForm.altText || '');
    formData.append('displayOrder', imageForm.displayOrder || 0);
    formData.append('enabled', imageForm.enabled);

    if (imageForm.imageFile) {
      formData.append('image', imageForm.imageFile);
    } else if (!editingImage) {
      alert("Please upload an image");
      return;
    }

    try {
      if (editingImage) {
        await apiCall(`/gallery/${editingImage}`, 'PUT', formData, true);
        alert('Image updated successfully!');
      } else {
        await apiCall('/gallery', 'POST', formData, true);
        alert('Image created successfully!');
      }
      fetchImages();
      setShowImageForm(false);
      setImageForm({ category: '', title: '', altText: '', imageUrl: '', displayOrder: 0, enabled: true });
    } catch (error) {
      alert('Failed to save image: ' + error.message);
    }
  };

  const handleDeleteImage = async (id) => {
    if (window.confirm('Are you sure you want to delete this image?')) {
      try {
        await apiCall(`/gallery/${id}`, 'DELETE');
        alert('Image deleted successfully!');
        fetchImages();
      } catch (error) {
        alert("Failed to delete: " + error.message);
      }
    }
  };

  const toggleImageStatus = (id) => {
    const updatedImages = images.map(img =>
      img.id === id ? { ...img, enabled: !img.enabled } : img
    );
    setImages(updatedImages);
    localStorage.setItem('galleryImages', JSON.stringify(updatedImages));
  };

  const filteredImages = selectedCategory === 'all'
    ? images
    : images.filter(img => img.category === selectedCategory);

  return (
    <div className="admin-gallery-management">
      <div className="section-header">
        <h2>🖼️ Gallery Management</h2>
        <p>Manage gallery categories and images</p>
      </div>

      {/* Categories Section */}
      <div className="card" style={{ marginBottom: '30px' }}>
        <div className="card-header">
          <h3>Categories</h3>
          <button onClick={handleAddCategory} className="btn btn-primary">
            ➕ Add Category
          </button>
        </div>
        <div className="card-content">
          {showCategoryForm && (
            <div className="form-card" style={{ marginBottom: '20px', padding: '20px', background: '#f8f9fa', borderRadius: '8px' }}>
              <h4>{editingCategory ? 'Edit Category' : 'Add New Category'}</h4>
              <div className="form-grid">
                <div className="form-group">
                  <label>Category Name (URL-friendly)</label>
                  <input
                    type="text"
                    value={categoryForm.name}
                    onChange={(e) => setCategoryForm({ ...categoryForm, name: e.target.value })}
                    placeholder="e.g., yoga, meditation"
                  />
                </div>
                <div className="form-group">
                  <label>Display Name</label>
                  <input
                    type="text"
                    value={categoryForm.displayName}
                    onChange={(e) => setCategoryForm({ ...categoryForm, displayName: e.target.value })}
                    placeholder="e.g., Yoga, Meditation"
                  />
                </div>
                <div className="form-group">
                  <label>Display Order</label>
                  <input
                    type="number"
                    value={categoryForm.displayOrder}
                    onChange={(e) => setCategoryForm({ ...categoryForm, displayOrder: parseInt(e.target.value) })}
                  />
                </div>
                <div className="form-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={categoryForm.enabled}
                      onChange={(e) => setCategoryForm({ ...categoryForm, enabled: e.target.checked })}
                    />
                    {' '}Enabled
                  </label>
                </div>
              </div>
              <div className="form-actions">
                <button onClick={handleSaveCategory} className="btn btn-success">Save Category</button>
                <button onClick={() => setShowCategoryForm(false)} className="btn btn-secondary">Cancel</button>
              </div>
            </div>
          )}

          <div className="table-wrapper">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Display Name</th>
                  <th>Order</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {categories.sort((a, b) => a.displayOrder - b.displayOrder).map(category => (
                  <tr key={category.id}>
                    <td>{category.name}</td>
                    <td>{category.displayName}</td>
                    <td>{category.displayOrder}</td>
                    <td>
                      <button
                        onClick={() => toggleCategoryStatus(category.id)}
                        className={`status-badge ${category.enabled ? 'active' : 'inactive'}`}
                      >
                        {category.enabled ? '✓ Enabled' : '✗ Disabled'}
                      </button>
                    </td>
                    <td>
                      <button onClick={() => handleEditCategory(category)} className="btn-icon">✏️</button>
                      <button onClick={() => handleDeleteCategory(category.id)} className="btn-icon">🗑️</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Images Section */}
      <div className="card">
        <div className="card-header">
          <h3>Gallery Images</h3>
          <button onClick={handleAddImage} className="btn btn-primary">
            ➕ Add Image
          </button>
        </div>
        <div className="card-content">
          {/* Filter */}
          <div className="filter-container" style={{ marginBottom: '20px' }}>
            <label>Filter by Category: </label>
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              style={{ padding: '8px', borderRadius: '4px', marginLeft: '10px' }}
            >
              <option value="all">All Categories</option>
              {categories.filter(c => c.enabled).map(cat => (
                <option key={cat.id} value={cat.name}>{cat.displayName}</option>
              ))}
            </select>
          </div>

          {showImageForm && (
            <div className="form-card" style={{ marginBottom: '20px', padding: '20px', background: '#f8f9fa', borderRadius: '8px' }}>
              <h4>{editingImage ? 'Edit Image' : 'Add New Image'}</h4>
              <div className="form-grid">
                <div className="form-group">
                  <label>Category *</label>
                  <select
                    value={imageForm.category}
                    onChange={(e) => setImageForm({ ...imageForm, category: e.target.value })}
                  >
                    <option value="">Select Category</option>
                    {categories.filter(c => c.enabled).map(cat => (
                      <option key={cat.id} value={cat.name}>{cat.displayName}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Title *</label>
                  <input
                    type="text"
                    value={imageForm.title}
                    onChange={(e) => setImageForm({ ...imageForm, title: e.target.value })}
                    placeholder="Image title"
                  />
                </div>
                <div className="form-group">
                  <label>Alt Text</label>
                  <input
                    type="text"
                    value={imageForm.altText}
                    onChange={(e) => setImageForm({ ...imageForm, altText: e.target.value })}
                    placeholder="Alternative text for accessibility"
                  />
                </div>
                <div className="form-group">
                  <label>Display Order</label>
                  <input
                    type="number"
                    value={imageForm.displayOrder}
                    onChange={(e) => setImageForm({ ...imageForm, displayOrder: parseInt(e.target.value) })}
                  />
                </div>
                <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                  <label>Upload Image *</label>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                  />
                  {imageForm.imageUrl && (
                    <div style={{ marginTop: '10px' }}>
                      <img
                        src={imageForm.imageUrl}
                        alt="Preview"
                        style={{
                          maxWidth: '200px',
                          maxHeight: '200px',
                          objectFit: 'cover',
                          borderRadius: '8px',
                          border: '2px solid #ddd'
                        }}
                      />
                    </div>
                  )}
                </div>
                <div className="form-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={imageForm.enabled}
                      onChange={(e) => setImageForm({ ...imageForm, enabled: e.target.checked })}
                    />
                    {' '}Enabled
                  </label>
                </div>
              </div>
              <div className="form-actions">
                <button onClick={handleSaveImage} className="btn btn-success">Save Image</button>
                <button onClick={() => setShowImageForm(false)} className="btn btn-secondary">Cancel</button>
              </div>
            </div>
          )}

          <div className="gallery-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: '20px' }}>
            {filteredImages.sort((a, b) => a.displayOrder - b.displayOrder).map(image => (
              <div key={image.id} className="gallery-item-card" style={{
                border: '1px solid #ddd',
                borderRadius: '8px',
                overflow: 'hidden',
                background: '#fff'
              }}>
                <div style={{ position: 'relative' }}>
                  <img
                    src={image.imageUrl}
                    alt={image.altText || image.title}
                    style={{
                      width: '100%',
                      height: '200px',
                      objectFit: 'cover'
                    }}
                  />
                  <div style={{
                    position: 'absolute',
                    top: '10px',
                    right: '10px',
                    background: image.enabled ? '#28a745' : '#dc3545',
                    color: 'white',
                    padding: '4px 8px',
                    borderRadius: '4px',
                    fontSize: '12px'
                  }}>
                    {image.enabled ? 'Active' : 'Disabled'}
                  </div>
                </div>
                <div style={{ padding: '15px' }}>
                  <h4 style={{ margin: '0 0 5px 0', fontSize: '16px' }}>{image.title}</h4>
                  <p style={{ margin: '0 0 10px 0', fontSize: '12px', color: '#666' }}>
                    Category: {categories.find(c => c.name === image.category)?.displayName || image.category}
                  </p>
                  <p style={{ margin: '0 0 10px 0', fontSize: '12px', color: '#666' }}>
                    Order: {image.displayOrder}
                  </p>
                  <div style={{ display: 'flex', gap: '10px' }}>
                    <button onClick={() => handleEditImage(image)} className="btn btn-sm btn-primary">Edit</button>
                    <button
                      onClick={() => toggleImageStatus(image.id)}
                      className={`btn btn-sm ${image.enabled ? 'btn-warning' : 'btn-success'}`}
                    >
                      {image.enabled ? 'Disable' : 'Enable'}
                    </button>
                    <button onClick={() => handleDeleteImage(image.id)} className="btn btn-sm btn-danger">Delete</button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {filteredImages.length === 0 && (
            <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
              <p>No images found. Click "Add Image" to upload your first image.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminGalleryManagement;
